/**
 * `/api/accounts/**` — the user's worlds and their character rosters. See `RosterController` /
 * `RosterDtos` for the authoritative shapes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';

export interface CharacterDto {
  id: number;
  accountId: number;
  name: string;
  classSlug: string;
  level: number;
  arcaneForce: number | null;
  sacredForce: number | null;
  sortOrder: number;
  /** REV4: server-fetched GMS rankings avatar image URL — null until a lookup finds a match. */
  avatarUrl: string | null;
}

export interface AccountDto {
  id: number;
  worldSlug: string;
  label: string;
  createdAt: string;
  /** REV7: the account's position in the user's drag-to-reorder list. */
  sortOrder: number;
  characters: CharacterDto[];
}

export interface CreateAccountInput {
  worldSlug: string;
  label: string;
}

export interface UpdateAccountInput {
  worldSlug?: string;
  label?: string;
}

export interface CreateCharacterInput {
  name: string;
  classSlug: string;
  level: number;
  arcaneForce?: number | null;
  sacredForce?: number | null;
}

export type UpdateCharacterInput = Partial<CreateCharacterInput>;

/**
 * REV12: same-origin avatar image proxy path — the backend streams the cached bytes of a
 * character's `avatarUrl` from here so the browser never hotlinks the GMS CDN directly (that
 * relaxed `img-src` requirement is what REV12 removes from the Caddyfiles). Callers only build
 * this when `avatarUrl != null`; a 404 (no/expired avatar) is handled by the `<img>`'s `onError`.
 */
export function avatarProxyPath(accountId: number, characterId: number): string {
  return `/api/accounts/${accountId}/characters/${characterId}/avatar.png`;
}

export const ACCOUNTS_QUERY_KEY = ['accounts'] as const;

export const accountsQuery = {
  queryKey: ACCOUNTS_QUERY_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<AccountDto[]>('/api/accounts', signal),
};

export function useAccounts() {
  return useQuery(accountsQuery);
}

function useInvalidateAccounts() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
}

export function useCreateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (input: CreateAccountInput) => apiSend<AccountDto>('POST', '/api/accounts', input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ accountId, input }: { accountId: number; input: UpdateAccountInput }) =>
      apiSend<AccountDto>('PATCH', `/api/accounts/${accountId}`, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAccount() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: (accountId: number) => apiSend<void>('DELETE', `/api/accounts/${accountId}`),
    onSuccess: () => invalidate(),
  });
}

export function useCreateCharacter() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ accountId, input }: { accountId: number; input: CreateCharacterInput }) =>
      apiSend<CharacterDto>('POST', `/api/accounts/${accountId}/characters`, input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateCharacter() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({
      accountId,
      characterId,
      input,
    }: {
      accountId: number;
      characterId: number;
      input: UpdateCharacterInput;
    }) => apiSend<CharacterDto>('PATCH', `/api/accounts/${accountId}/characters/${characterId}`, input),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteCharacter() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ accountId, characterId }: { accountId: number; characterId: number }) =>
      apiSend<void>('DELETE', `/api/accounts/${accountId}/characters/${characterId}`),
    onSuccess: () => invalidate(),
  });
}

/** REV4: re-runs the server-side GMS rankings avatar lookup on demand. */
export function useRefreshAvatar() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ accountId, characterId }: { accountId: number; characterId: number }) =>
      apiSend<CharacterDto>('POST', `/api/accounts/${accountId}/characters/${characterId}/avatar-refresh`),
    onSuccess: () => invalidate(),
  });
}

export function useReorderCharacters() {
  const invalidate = useInvalidateAccounts();
  return useMutation({
    mutationFn: ({ accountId, characterIds }: { accountId: number; characterIds: number[] }) =>
      apiSend<CharacterDto[]>('PATCH', `/api/accounts/${accountId}/characters/order`, { characterIds }),
    onSuccess: () => invalidate(),
  });
}

/**
 * REV7: {@code PATCH /api/accounts/order} — drag reorder of the account cards on /characters.
 * Optimistic apply + rollback-on-error, the same idiom `useCompleteTask` (checklist.ts) uses: the
 * `['accounts']` cache is reordered immediately so the drop feels instant, a failed PATCH restores
 * the previous order and raises a toast, and a settle always re-syncs with the server regardless of
 * outcome.
 */
export function useReorderAccounts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountIds: number[]) =>
      apiSend<AccountDto[]>('PATCH', '/api/accounts/order', { accountIds }),
    onMutate: async (accountIds) => {
      await queryClient.cancelQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      const previous = queryClient.getQueryData<AccountDto[]>(ACCOUNTS_QUERY_KEY);
      if (previous) {
        const byId = new Map(previous.map((account) => [account.id, account]));
        const reordered = accountIds
          .map((id) => byId.get(id))
          .filter((account): account is AccountDto => account != null);
        queryClient.setQueryData<AccountDto[]>(ACCOUNTS_QUERY_KEY, reordered);
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ACCOUNTS_QUERY_KEY, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that change.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    },
  });
}
